import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import IndividualRehabPlanTwoForm from "./IndividualRehabPlanTwoForm";
import { createIndividualRehabPlanTwo } from "../../store/slices/individualRehabPlanTwoSlice";
import { fetchPatient } from "../../store/slices/patientsSlice";

const AddIndividualRehabPlanTwo = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { caseId, patientId } = state || {};

  const [formData, setFormData] = useState({
    patient_id: patientId || "",
    case_id: caseId || "",
    recovery_capitals: [
      { id: Date.now() + 1, personal_capital: "", social_family: "", community: "", financial_housing: "" },
    ],
    strengths_growths: [
      { id: Date.now() + 2, strengths: "", areas_for_growth: "" },
    ],
    objectives: [
      { id: Date.now() + 3, objective: "", action_steps: "", environment_of_choice: "", frequency_duration: "", target_date: "", obj_status: "" },
    ],
    self_reviews: [
      { id: Date.now() + 4, review_date: "", client_self_assessment: "", prp_observation: "", next_steps: "" },
    ],
    smart_goals: [
      {
        id: Date.now() + 5,
        rehab_goal_statement: "", start_date: "", end_date: "",
        smart_specific_client: "", smart_specific_prp: "",
        smart_measurable_client: "", smart_measurable_prp: "",
        smart_achievable_client: "", smart_achievable_prp: "",
        smart_relevant_client: "", smart_relevant_prp: "",
        smart_timebound_client: "", smart_timebound_prp: "",
      },
    ],
  });

  const [patientData, setPatientData] = useState(null);

  useEffect(() => {
    if (patientId) {
      dispatch(fetchPatient(patientId))
        .unwrap()
        .then((patient) => setPatientData(patient))
        .catch((err) => console.error("Failed to fetch patient details:", err));
    }
  }, [dispatch, patientId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await dispatch(createIndividualRehabPlanTwo(formData)).unwrap();
      Swal.fire({
        title: "Success!",
        text: "Individual Rehabilitation Plan (2) added successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
      navigate(`/individual-rehab-plan-two/edit/${res.id}`, {
        state: { patientId: patientId || res.patient_id, caseId: caseId || res.case_id },
      });
    } catch (err) {
      let errorMessage = "Failed to add Individual Rehabilitation Plan (2)";
      if (typeof err === "string") errorMessage = err;
      else if (typeof err === "object" && err !== null) errorMessage = Object.values(err).flat().join("\n");
      Swal.fire({ title: "Error", text: errorMessage, icon: "error" });
    }
  };

  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <Breadcrumbs />

        {patientData && (
          <div className="card mb-4">
            <div className="card-body bg-light-primary rounded-4">
              <div className="row g-3">
                <div className="col-md-3">
                  <span className="fw-semibold text-secondary small d-block">Client Name:</span>
                  <span className="fw-bold text-dark">{patientData.first_name} {patientData.last_name}</span>
                </div>
                <div className="col-md-3">
                  <span className="fw-semibold text-secondary small d-block">MR Number:</span>
                  <span className="fw-bold text-dark">{patientData.mr_no || "N/A"}</span>
                </div>
                <div className="col-md-3">
                  <span className="fw-semibold text-secondary small d-block">Date of Birth:</span>
                  <span className="fw-bold text-dark">
                    {patientData.dob ? new Date(patientData.dob).toLocaleDateString("en-GB") : "N/A"}
                  </span>
                </div>
                <div className="col-md-3">
                  <span className="fw-semibold text-secondary small d-block">Gender:</span>
                  <span className="fw-bold text-dark capitalize">{patientData.gender || "N/A"}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-header border-bottom">
            <h5 className="mb-0">Add Individual Rehabilitation Plan (2)</h5>
            <small className="text-muted">Client-Centred SMART Rehabilitation Plan (CARF-Aligned)</small>
          </div>
          <form onSubmit={handleSubmit}>
            <IndividualRehabPlanTwoForm formData={formData} setFormData={setFormData} />
            <div className="row px-4 pb-4">
              <div className="col-md-2 ms-auto">
                <button type="submit" className="btn btn-primary w-100 sticky-form-submit">
                  <i className="ri-save-3-line me-1"></i> Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </MainLayout>
  );
};

export default AddIndividualRehabPlanTwo;
